import * as React from 'react';
import { useEffect, useState } from 'react';
import { useLocalization } from '@bumble/localization/components';
import { useRouter } from 'next/router';
import supportedLanguages from 'lib/lexemes/supported-languages';
import { handleCookieConsent, onUsePrivacyManagerHandler } from 'lib/cookie-consent';
import JoinHivesPage from 'components/join-hives/join-hives-page';
import type { FooterLinksProps } from 'components/footer-links/footer-links';
import type { SocialLinksProps } from 'components/social-links/social-links';
import { App } from 'components/app/app';

type Props = {
    footerLinks: FooterLinksProps['items'];
    socialLinks: SocialLinksProps['socialLinks'];
    copyright: string;
};

const IndexPage: React.FC<Props> = function ({ footerLinks, socialLinks, copyright }) {
    const Localization = useLocalization();
    const router = useRouter();
    const [isMobile, setIsMobile] = useState(false);
    const [ref, setRef] = useState<string>('web-direct-cta');

    useEffect(() => {
        setIsMobile(window.outerWidth < 800);

        function handleResize() {
            setIsMobile(window.outerWidth < 800);
        }

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (router.isReady && router.query.ref) {
            setRef(router.query?.ref as string);
        }
    }, [router.isReady, router.query.ref]);

    // configure cookie consent click handlers
    React.useEffect(() => {
        if (process.env.NODE_ENV === 'production') {
            const listenerId = __tcfapi?.('addEventListener', 2, handleCookieConsent);

            return () => {
                if (listenerId) {
                    __tcfapi?.('removeEentListener', 2, null, listenerId);
                }
            };
        } else {
            return;
        }
    }, []);
    useEffect(onUsePrivacyManagerHandler, []);

    const title = Localization.get('bumblesite@join_plans.page_header');
    const text = Localization.get('bumblesite@join_plans.page_subcopy');
    const cta = Localization.get('bumblesite@join_plans.get_started_cta');
    const bullet1Header = Localization.get('bumblesite@join_plans.bullet_1.header');
    const bullet1Text = Localization.get('bumblesite@join_plans.bullet_1.text');
    const bullet2Header = Localization.get('bumblesite@join_plans.bullet_2.header');
    const bullet2Text = Localization.get('bumblesite@join_plans.bullet_2.text');
    const bullet3Header = Localization.get('bumblesite@join_plans.bullet_3.header');
    const bullet3Text = Localization.get('bumblesite@join_plans.bullet_3.text');
    // const cta = 'Download the app';
    // const bullet1Header = 'Create a profile';
    // const bullet1Text = 'Or, log in with an existing Bumble account and we’ll do the rest!';
    // const bullet2Header = 'Join a group or make your own';
    // const bullet2Text = 'Use Plans to see what’s on nearby, or suggest something fun.';
    // const bullet3Header = 'Meet your new crew IRL';
    // const bullet3Text = 'Forget endless group chats, set a date and time and get out there!';

    return (
        <App>
            <JoinHivesPage
                isMobile={isMobile}
                title={title}
                text={text}
                bullets={[
                    // design team asked to swap bullets 1 and 2
                    { header: bullet2Header, text: bullet2Text },
                    { header: bullet1Header, text: bullet1Text },
                    { header: bullet3Header, text: bullet3Text },
                ]}
                cta={{
                    text: cta,
                    url: `https://bumbleforfriends.com/aa/landto?ref=${ref}`,
                }}
                appStore={{
                    url: `https://bumbleforfriends.com/install?d=Iphone&ref=${ref}`,
                    text: Localization.get('bumblesite@join_hives.app_store_button'),
                }}
                playStore={{
                    url: `https://bumbleforfriends.com/install?d=Android&ref=${ref}`,
                    text: Localization.get('bumblesite@join_hives.play_store_button'),
                }}
                footerLinks={footerLinks}
                socialLinks={socialLinks}
                copyright={copyright}
            />
        </App>
    );
};

export default IndexPage;

export { getStaticProps } from 'server/page-props';

export async function getStaticPaths() {
    return {
        paths: supportedLanguages.map((lang) => ({
            params: {
                lang,
            },
        })),
        fallback: false,
    };
}

// function escapeHTML(str: string) {
//     return str
//         .replace(/&/g, '&amp;')
//         .replace(/</g, '&lt;')
//         .replace(/>/g, '&gt;')
//         .replace(/"/g, '&quot;');
// }
